import React, { useState, useEffect } from 'react';

interface TermsData {
  acceptTerms: boolean;
  acceptPrivacy: boolean;
  acceptPersonalData: boolean;
  acceptPlatformTerms: boolean;
}

interface TermsAndConditionsProps {
  terms: TermsData;
  onTermsChange: (field: keyof TermsData, value: boolean) => void;
  disabled?: boolean;
  className?: string;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  terms,
  onTermsChange,
  disabled = false,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Проверяем, выбраны ли все вложенные чекбоксы
  const areAllSubTermsAccepted = terms.acceptPrivacy && terms.acceptPersonalData && terms.acceptPlatformTerms;

  // Обработчик для главного чекбокса "все условия"
  const handleMainTermsChange = (checked: boolean) => {
    onTermsChange('acceptTerms', checked);
    
    // Если выбираем главный чекбокс, выбираем все вложенные
    if (checked) {
      onTermsChange('acceptPrivacy', true);
      onTermsChange('acceptPersonalData', true);
      onTermsChange('acceptPlatformTerms', true);
    }
  };

  // Обработчик для вложенных чекбоксов
  const handleSubTermsChange = (field: keyof Omit<TermsData, 'acceptTerms'>, checked: boolean) => {
    onTermsChange(field, checked);
    
    // Если все вложенные выбраны, выбираем главный
    if (checked) {
      const newSubTerms = {
        ...terms,
        [field]: checked
      };
      
      if (newSubTerms.acceptPrivacy && newSubTerms.acceptPersonalData && newSubTerms.acceptPlatformTerms) {
        onTermsChange('acceptTerms', true);
      }
    } else {
      // Если хотя бы один вложенный снят, снимаем главный
      onTermsChange('acceptTerms', false);
    }
  };

  // Автоматически выбираем главный чекбокс, если все вложенные выбраны
  useEffect(() => {
    if (areAllSubTermsAccepted && !terms.acceptTerms) {
      onTermsChange('acceptTerms', true);
    }
  }, [areAllSubTermsAccepted, terms.acceptTerms, onTermsChange]);

  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={terms.acceptTerms}
            onChange={(e) => !disabled && handleMainTermsChange(e.target.checked)}
            disabled={disabled}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
                                                                 <span className={`ml-2 text-sm text-white ${disabled ? 'opacity-50' : ''}`}>
             Я принимаю <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline cursor-pointer hover:text-primary-600">все условия</a>
           </span>
        </div>
        <button
          type="button"
          onClick={() => !disabled && setIsExpanded(!isExpanded)}
          disabled={disabled}
          className="flex items-center justify-center p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <svg 
            className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''} ${disabled ? 'opacity-50' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isExpanded && (
        <div className="mt-3 ml-6 space-y-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={terms.acceptPrivacy}
              onChange={(e) => !disabled && handleSubTermsChange('acceptPrivacy', e.target.checked)}
              disabled={disabled}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className={`ml-2 text-sm text-white ${disabled ? 'opacity-50' : ''}`}>
              Я принимаю <a href="/policy" target="_blank" rel="noopener noreferrer" className="underline cursor-pointer hover:text-primary-600">Политику конфиденциальности</a>
            </span>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={terms.acceptPersonalData}
              onChange={(e) => !disabled && handleSubTermsChange('acceptPersonalData', e.target.checked)}
              disabled={disabled}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className={`ml-2 text-sm text-white ${disabled ? 'opacity-50' : ''}`}>
              Даю согласие на <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline cursor-pointer hover:text-primary-600">Обработку персональных данных</a>
            </span>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={terms.acceptPlatformTerms}
              onChange={(e) => !disabled && handleSubTermsChange('acceptPlatformTerms', e.target.checked)}
              disabled={disabled}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className={`ml-2 text-sm text-white ${disabled ? 'opacity-50' : ''}`}>
              Принимаю <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline cursor-pointer hover:text-primary-600">Условия использования платформы</a>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TermsAndConditions;
